import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrencies } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const currentCurrency = state.context.session.currency;
    return {
        cart,
        currentCurrency,
        currencies: getCurrencies(state),
    };
};

const mapDispatchToProps = {
    setCurrency,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutCurrencyStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    changeCurrencyLink?: LinkPresentationProps;
    currencyDisplayGridItem?: GridItemProps;
    currencyText?: TypographyProps;
    currencySelect?: SelectPresentationProps;
}

export const checkoutCurrencyStyles: CheckoutCurrencyStyles = {
    container: {
        gap: 20,
        css: css`
            margin-bottom: 58px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            background-color: #f6f5fb;
            height: 53px;
        `,
    },
    headingText: {
        variant: "h5",
        size: 18,
        css: css`
            margin-bottom: 0;
            font-weight: 900;
            letter-spacing: 2px;
            line-height: 22px;
            text-transform: uppercase;
        `,
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    changeCurrencyLink: {
        color: "danger.main",
        typographyProps: {
            css: css`
                font-size: 16px;
                line-height: 34px;
                text-decoration: underline;
            `,
        },
        css: css`
            margin-left: 15px;
        `,
    },
    currencyDisplayGridItem: {
        width: 12,
        css: css`
            flex-direction: row;
        `,
    },
    currencyText: {
        as: "p",
        css: css`
            font-size: 14px;
        `,
    },
    currencySelect: {
        iconProps: {
            color: "danger.main",
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 250px;
                border-radius: 4px;
                box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
            `,
        },
    },
};

const styles = checkoutCurrencyStyles;

const CheckoutCurrency: FC<Props> = ({ cart, currentCurrency, currencies, setCurrency }) => {
    if (!currentCurrency) {
        return null;
    }

    const [isEditingCurrency, setIsEditingCurrency] = useState(false);

    const changeCurrencyClickHandler = () => setIsEditingCurrency(!isEditingCurrency);
    const selectCurrencyChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrency({ currencyId: event.currentTarget.value });
        setIsEditingCurrency(false);
    };
    const currencyText = `${currentCurrency.currencySymbol} ${currentCurrency.description}`;

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={!cart}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.headingText} as="h3">
                        {translate("Currency Information")}
                    </Typography>
                    {!!cart && (
                        <Link
                            {...styles.changeCurrencyLink}
                            type="button"
                            onClick={changeCurrencyClickHandler}
                            data-test-selector="checkoutShipping_selectSavedAddress"
                        >
                            {!isEditingCurrency && translate("Change")}
                            {isEditingCurrency && translate("Cancel")}
                        </Link>
                    )}
                </GridItem>
                <GridItem {...styles.currencyDisplayGridItem}>
                    {!isEditingCurrency && <Typography {...styles.currencyText}>{currencyText}</Typography>}
                    {isEditingCurrency && (
                        <Select
                            {...styles.currencySelect}
                            value={currentCurrency.id}
                            onChange={selectCurrencyChangeHandler}
                        >
                            {currencies?.map(c => (
                                <option value={c.id} key={c.id}>
                                    {`${c.currencySymbol} ${c.description}`}
                                </option>
                            ))}
                        </Select>
                    )}
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutCurrency),
    definition: {
        group: "Checkout" as any,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Currency",
    },
};

export default widgetModule;
